import type { Receipt, ReceiptStore } from "./types";

const STORAGE_KEY = "proofly.receipts.v1";

function readFromStorage(): Receipt[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    // Trust the shape — we control writes. Cast minimally.
    return parsed as Receipt[];
  } catch {
    return [];
  }
}

function writeToStorage(receipts: Receipt[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(receipts));
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(reader.error ?? new Error("Failed to read file"));
    };
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        resolve(result);
      } else {
        reject(new Error("FileReader returned non-string result"));
      }
    };
    reader.readAsDataURL(file);
  });
}

function generateId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Fallback for older environments
  return `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function createLocalStorageReceiptStore(): ReceiptStore {
  const listeners = new Set<() => void>();

  // Cached snapshot. Must be a stable reference between mutations so that
  // useSyncExternalStore's Object.is check doesn't see a new array on every
  // render and trigger React error #185 (infinite re-render loop).
  let snapshot: Receipt[] = readFromStorage();

  function emit(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  function commit(next: Receipt[]): void {
    snapshot = next;
    writeToStorage(next);
    emit();
  }

  // Sync across tabs: if another tab mutates the same key, rebuild the
  // snapshot from storage and notify listeners.
  if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
      if (event.key !== STORAGE_KEY) return;
      snapshot = readFromStorage();
      emit();
    });
  }

  return {
    list(): Receipt[] {
      return snapshot;
    },

    get(id: string): Receipt | undefined {
      return snapshot.find((r) => r.id === id);
    },

    async add(input: {
      file: File;
      linkedExpenseId?: string;
      linkedInvoiceId?: string;
    }): Promise<Receipt> {
      const { file, linkedExpenseId, linkedInvoiceId } = input;
      const url = await readFileAsDataUrl(file);
      const receipt: Receipt = {
        id: generateId(),
        filename: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        uploadedAt: new Date().toISOString(),
        url,
        linkedExpenseId,
        linkedInvoiceId,
      };
      commit([receipt, ...snapshot]);
      return receipt;
    },

    remove(id: string): void {
      const next = snapshot.filter((r) => r.id !== id);
      if (next.length !== snapshot.length) commit(next);
    },

    link(
      id: string,
      link: { linkedExpenseId?: string; linkedInvoiceId?: string },
    ): void {
      let changed = false;
      const next = snapshot.map((r) => {
        if (r.id !== id) return r;
        changed = true;
        return {
          ...r,
          linkedExpenseId: link.linkedExpenseId ?? r.linkedExpenseId,
          linkedInvoiceId: link.linkedInvoiceId ?? r.linkedInvoiceId,
        };
      });
      if (changed) commit(next);
    },

    async refreshUrl(id: string): Promise<string | null> {
      // data: URLs don't expire — just hand back what's in storage.
      const r = snapshot.find((x) => x.id === id);
      return r?.url ?? null;
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
